import {
  advisorProfileCompletenessItems,
  advisorProfileCompletenessScore,
  type AdvisorProfileCompletenessInput,
} from '@src/components/sco/Advisors/utils/advisorProfileCompleteness'

export type AdvisorSupplyReadinessInput = AdvisorProfileCompletenessInput & {
  isApproved?: boolean | null
  statusKey?: string | null
  hourlyRate?: number | string | null
  isSessionEnabled?: boolean | null
  canAcceptSessions?: boolean | null
  stripeConnectStatusKey?: string | null
  preferredCompensationTypeKeys?: Array<string | null> | null
}

export type SupplyReadinessStep = {
  key: 'live' | 'complete' | 'rates' | 'connect'
  label: string
  done: boolean
  optional?: boolean
  hrefKey: 'ownProfile' | 'sessionSettings' | 'payouts'
  detail?: string
  warning?: string
}

const COMPLETENESS_TARGET = 80

const wantsCashSessions = (profile: AdvisorSupplyReadinessInput | null | undefined) => {
  const keys = (profile?.preferredCompensationTypeKeys || []).map(k => (k || '').toLowerCase())
  if (keys.includes('cash')) {
    return true
  }
  if (profile?.isSessionEnabled) {
    return true
  }
  const rate = Number(profile?.hourlyRate)
  return Number.isFinite(rate) && rate > 0
}

export function advisorSupplyReadinessSteps(
  profile: AdvisorSupplyReadinessInput | null | undefined,
  opts?: { pictureUrl?: string | null; shortBio?: string | null }
): SupplyReadinessStep[] {
  const items = advisorProfileCompletenessItems(profile, opts)
  const { percent } = advisorProfileCompletenessScore(items)
  const missing = items.filter(item => !item.done).map(item => item.label)
  const cash = wantsCashSessions(profile)
  const ratesDone = !!profile?.canAcceptSessions
  const connectKey = (profile?.stripeConnectStatusKey || 'not_started').toLowerCase()
  const connectDone = connectKey === 'enabled'
  const sessionsOnWithoutPayout = !!profile?.isSessionEnabled && !connectDone

  return [
    {
      key: 'live',
      label: 'Profile live',
      done: !!profile?.isApproved,
      hrefKey: 'ownProfile',
      detail: profile?.isApproved
        ? 'Visible in the directory'
        : profile?.statusKey === 'applied'
          ? 'Pending admin review'
          : 'Apply to go live',
    },
    {
      key: 'complete',
      label: `Profile completeness (${percent}%)`,
      done: percent >= COMPLETENESS_TARGET,
      hrefKey: 'ownProfile',
      detail:
        percent >= 100
          ? 'Ready for founders'
          : missing.length
            ? `${percent >= COMPLETENESS_TARGET ? 'Ready for founders' : `Aim for ${COMPLETENESS_TARGET}%`} · missing ${missing.join(', ')}`
            : `Aim for ${COMPLETENESS_TARGET}%`,
    },
    {
      key: 'rates',
      label: 'Session rates',
      done: cash ? ratesDone : true,
      optional: !cash,
      hrefKey: 'sessionSettings',
      detail: cash
        ? ratesDone
          ? 'Founders can book paid calls'
          : 'Set a rate and turn sessions on'
        : 'Optional unless you take cash calls',
    },
    {
      key: 'connect',
      label: 'Connect payouts',
      done: connectDone,
      hrefKey: 'payouts',
      detail: connectDone ? 'Ready to receive payouts' : 'Required before you can withdraw — booking still works',
      warning: sessionsOnWithoutPayout
        ? 'Sessions are on. Connect Stripe before your first payout.'
        : undefined,
    },
  ]
}

export function advisorSupplyReadinessScore(steps: SupplyReadinessStep[]) {
  const required = steps.filter(s => !s.optional)
  const done = required.filter(s => s.done).length
  const total = required.length
  return {
    done,
    total,
    percent: total === 0 ? 0 : Math.round((100 * done) / total),
  }
}
